import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { useCallbackOnInitAndResizeHook } from '../../../../../shared/src/hooks/use-callback-on-init-and-resize.hook';
import { FlyingSliderArrow, FlyingSliderArrowTypesEnum } from '../../../../../shared/src/components/FlyingSliderArrow';
import { Navigation } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
import { PromotionStripItem } from '../PromotionStripItem';
import './PromotionStrip.css';
import {
    GtmEventComponent,
    GtmEventLocation
} from '../../../../../shared/src/components/google-tag-manager/enums/gtm-event-enum';
import { useSwiperTracking } from '../../../../../shared/src/hooks/use-swiper-tracking.hook';
import { useCurrentVerticalData } from '../../../../../shared/src/hooks/use-current-vertical-data.hook';

export const PromotionsStrip = ({
    promotions,
    parentWidth,
    isMobile,
    isLoading
}) => {
    const [refsReady, setRefsReady] = useState(false);
    const modules = [Navigation];
    const [visibleItemsNum, setVisibleItemsNum] = useState(0);
    const {
        pamGameVerticalID,
        currentVerticalType,
    } = useCurrentVerticalData();
    const {
        onTouchStartHandler,
        onArrowClickHandler,
        onSlideChangeHandler,
    } = useSwiperTracking({
        location: GtmEventLocation.GAME_GRID,
        component: GtmEventComponent.GRID_STRIP,
        source: 'promotions',
        verticalId: pamGameVerticalID,
        verticalType: currentVerticalType,
        grid_strip_type: 'promotions',
    });

    const prevRef = useRef(null);
    const nextRef = useRef(null);
    const slidesPerView = isMobile ? 2 : 3;
    const handleOnInit = (swiper) => {
        if (!isMobile) {
            swiper.params.navigation.prevEl = prevRef.current;
            swiper.params.navigation.nextEl = nextRef.current;
            swiper.navigation.init();
            swiper.navigation.update();
        }
    };
    // Wait until both arrow buttons are actually in the DOM.
    // Swiper’s navigation.init() needs real nodes –
    // if we mount <Swiper> before prevRef/nextRef are ready,
    // navigation won’t attach and arrows will be inactive.
    useEffect(() => {
        if (prevRef.current && nextRef.current) {
            setRefsReady(true);
        }
    }, [prevRef.current, nextRef.current]);

    const itemsGap = isMobile ? 18 : 22;
    const getDesktopItemWidth = useCallback(() => {
        const windowWidth = window.innerWidth;
        let itemWidth = 250;
        if (windowWidth < 1440) {
            itemWidth = 200;
        } else if (windowWidth >= 1920) {
            itemWidth = 300;
        }
        return itemWidth;
    }, []);

    const visibleItemsNumCalc = useCallback(() => {
        const winnerItemWidth = isMobile ? 120 : getDesktopItemWidth();
        const wrapperWidth = parentWidth || window.innerWidth;
        setVisibleItemsNum(Math.ceil(wrapperWidth / (winnerItemWidth + itemsGap)));
    }, [isMobile, parentWidth]);

    useCallbackOnInitAndResizeHook({ callback: visibleItemsNumCalc });

    const promotionsToShow = useMemo(() => {
        const numOfPromotions = promotions?.length;

        if (!numOfPromotions) {
            return [];
        }

        const timestamp = Date.now();
        const refilledPromos = [];
        for (let i = 0; i < numOfPromotions; i++) {
            refilledPromos.push({
                key: `${timestamp}-${i}`,
                data: promotions[i],
            });
        }

        return refilledPromos;
    }, [promotions, visibleItemsNum]);

    const arePromotionsReady = !!promotionsToShow?.length;

    return (
        <>
            {arePromotionsReady && (
                <div className="promotions-strip__wrapper">
                    <FlyingSliderArrow
                        extraClass="promotions-strip-button"
                        arrowType={FlyingSliderArrowTypesEnum.PREV}
                        ref={prevRef}
                        onClick={onArrowClickHandler}
                    />
                    {refsReady && (
                        <Swiper
                            className="promotions-strip__swiper  promotions-strip__items"
                            modules={modules}
                            spaceBetween={20}
                            slidesPerView="auto"
                            breakpoints={{
                                768: {
                                    slidesPerView,
                                    spaceBetween: 20,
                                },
                            }}
                            speed={1500}
                            navigation={false}
                            onInit={handleOnInit}
                            onSlideChangeTransitionEnd={onSlideChangeHandler}
                            onTouchStart={onTouchStartHandler}
                        >
                            {promotionsToShow.map((promotion) => (
                                <SwiperSlide key={promotion.key}>
                                    <PromotionStripItem
                                        promotion={promotion.data}
                                        isLoading={isLoading}
                                    />
                                </SwiperSlide>
                            ))}
                        </Swiper>
                    )}
                    <FlyingSliderArrow
                        extraClass="promotions-strip-button"
                        arrowType={FlyingSliderArrowTypesEnum.NEXT}
                        ref={nextRef}
                        onClick={onArrowClickHandler}
                    />
                </div>
            )}
        </>
    );
};
